
import React, { useState, useRef } from 'react';
import { User, Camera, LogOut, Gem, ChevronRight, Edit } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/utils/toast';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn } from '@/lib/utils';
import AuraPointsSummary from './AuraPointsSummary';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { mobileAnimations } from '@/utils/mobile-animations';

interface ProfileSectionProps {
  initialTab?: string;
}

const nameSchema = z.object({
  displayName: z.string().min(2, "Name must be at least 2 characters")
});

const ProfileSection: React.FC<ProfileSectionProps> = ({ initialTab = "profile" }) => {
  const { currentUser, logout, userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof nameSchema>>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      displayName: currentUser?.displayName || ''
    }
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast.success("Logged out successfully");
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error("Logout failed");
    }
  };
  
  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = async (data: z.infer<typeof nameSchema>) => {
    if (!currentUser) return;
    
    setIsUpdatingProfile(true);
    try {
      await updateProfile({ displayName: data.displayName });
      setIsEditingName(false);
      toast.success("Your name has been updated");
    } catch (error) {
      console.error('Failed to update name:', error);
      toast.error("Failed to update profile");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image is too large (max 2MB)");
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setIsUpdatingProfile(true);
    try {
      // Convert image to base64 to update Firebase Auth profile
      const reader = new FileReader();
      reader.onload = async (event) => {
        const photoURL = event.target?.result as string;
        
        // Update profile with the new image
        await updateProfile({ photoURL });
        toast.success("Profile picture updated");
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to update profile picture:', error);
      toast.error("Failed to update profile picture");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  if (!currentUser) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-10 px-4 text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Sign in to view your profile</h2>
        <p className="text-muted-foreground mb-6">You need to be logged in to view and manage your profile</p>
        <div className="flex gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/login')}
            className={cn("font-bold", isDark ? "text-white" : "text-black")}
          >
            Sign In
          </Button>
          <Button 
            onClick={() => navigate('/signup')}
            className="font-bold"
          >
            Create Account
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      variants={mobileAnimations.profileCard}
      initial="hidden"
      animate="visible"
      className="w-full mx-auto"
    >
      {/* Profile Card */}
      <Card className={cn(
        "overflow-hidden rounded-xl border shadow-sm",
        isDark 
          ? "bg-gradient-to-b from-[#151833] to-[#1A1F38] border-blue-900/30" 
          : "bg-white border-blue-100/50"
      )}>
        {/* Profile Banner */}
        <div className={cn(
          "h-24 bg-gradient-to-r",
          isDark 
            ? "from-[#292D67] to-[#6C41E0]"
            : "from-[#D3E4FD] to-[#9b87f5]"
        )} />
        
        <CardContent className="pt-0">
          {/* Profile Info */}
          <div className={cn(
            "flex flex-col sm:flex-row gap-4 -mt-12 sm:items-end px-4",
            isMobile ? "items-center" : ""
          )}>
            <div className="relative z-10">
              <Avatar className={cn(
                "h-24 w-24 border-4 cursor-pointer group shadow-md",
                isDark 
                  ? "border-[#151833] ring-2 ring-blue-500/20" 
                  : "border-white ring-2 ring-blue-400/20"
              )}
              onClick={handleProfilePictureClick}>
                <AvatarImage src={currentUser?.photoURL || undefined} />
                <AvatarFallback className={cn(
                  "text-2xl font-bold",
                  isDark 
                    ? "bg-blue-900/30 text-blue-400" 
                    : "bg-blue-100 text-blue-600"
                )}>
                  {getInitials(currentUser?.displayName)}
                </AvatarFallback>
                <div className={cn(
                  "absolute inset-0 bg-black/30 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity",
                  isUpdatingProfile ? "opacity-100" : ""
                )}>
                  {isUpdatingProfile ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Camera size={20} className="text-white" />
                  )}
                </div>
              </Avatar>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={handleProfilePictureChange}
              />
              <Button
                size="icon"
                variant="secondary"
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full shadow-md"
                onClick={handleProfilePictureClick}
              >
                <Camera size={14} />
              </Button>
            </div>
            
            <div className={cn(
              "flex-1 z-10",
              isMobile ? "text-center sm:text-left" : ""
            )}>
              {isEditingName ? (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSaveName)} className="space-y-2">
                    <FormField
                      control={form.control}
                      name="displayName"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input 
                              {...field} 
                              placeholder="Enter your name"
                              className={cn(
                                "h-9",
                                isDark ? "bg-blue-900/20 border-blue-900/40" : ""
                              )}
                              disabled={isUpdatingProfile}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        size="sm" 
                        disabled={isUpdatingProfile}
                        className="text-xs"
                      >
                        {isUpdatingProfile ? (
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Saving
                          </span>
                        ) : "Save"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setIsEditingName(false)}
                        className="text-xs"
                        disabled={isUpdatingProfile}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : (
                <>
                  <div className={cn(
                    "flex items-center",
                    isMobile ? "justify-center sm:justify-start" : ""
                  )}>
                    <h3 className={cn(
                      "font-bold text-xl",
                      isDark ? "text-white" : "text-slate-800"
                    )}>
                      {currentUser?.displayName || 'Anonymous User'}
                    </h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleEditName}
                      className="ml-2 p-1 h-6 w-6 rounded-full"
                    >
                      <Edit size={12} />
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {currentUser?.email || 'No email available'}
                  </p>
                </>
              )}
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className={cn(
                "gap-1 text-xs h-8", 
                isMobile ? "mx-auto sm:ml-auto sm:mr-0" : "ml-auto",
                isDark 
                  ? "text-red-400 hover:text-red-300 bg-red-900/10 border-red-900/30" 
                  : "text-red-500 hover:text-red-600 hover:border-red-400"
              )}
            >
              <LogOut size={14} />
              Sign out
            </Button>
          </div>
          
          <Separator className={cn(
            "my-6", 
            isDark ? "bg-blue-900/30" : "bg-blue-100/70"
          )} />
          
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className={cn(
                "rounded-xl p-4 flex items-center gap-3",
                isDark ? "bg-blue-900/10" : "bg-blue-50/50"
              )}>
                <Gem size={20} className={isDark ? "text-blue-400" : "text-blue-600"} />
                <div>
                  <p className="text-sm font-medium">Aura Points</p>
                  <p className="text-sm text-muted-foreground">
                    {userProfile?.auraPoints || 0} points
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline"
              className={cn(
                "w-full justify-between",
                isDark 
                  ? "bg-blue-900/10 hover:bg-blue-900/20 border-blue-900/30 text-white" 
                  : "bg-blue-50/50 hover:bg-blue-100/50 text-blue-700"
              )}
              onClick={() => navigate('/points')}
            >
              <div className="flex items-center gap-2">
                <Gem size={16} />
                View Points History
              </div>
              <ChevronRight size={16} />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <div className="mt-6">
        <AuraPointsSummary />
      </div>
    </motion.div>
  );
}

export default ProfileSection;
